<template>
  <div
    class="login-page"
    :style="{ backgroundImage: `url(${backgroundImage})` }"
  >
    <div class="login-page__panel">
      <div class="login-page__heading">
        <h1>乌昌海关业务运行可视化</h1>
      </div>

      <form class="login-form" @submit.prevent="handleLogin">
        <label class="login-form__field">
          <span class="login-form__label">账号</span>
          <span class="login-form__control">
            <span class="login-form__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M12 12a4.5 4.5 0 1 0-4.5-4.5A4.5 4.5 0 0 0 12 12Zm0 2.25c-3.49 0-6.75 1.71-6.75 4.5V20h13.5v-1.25c0-2.79-3.26-4.5-6.75-4.5Z" />
              </svg>
            </span>
            <input
              v-model.trim="loginForm.username"
              type="text"
              autocomplete="username"
              placeholder="请输入账号"
            >
          </span>
        </label>

        <label class="login-form__field">
          <span class="login-form__label">密码</span>
          <span class="login-form__control">
            <span class="login-form__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M17.25 10.5h-.75V8.25a4.5 4.5 0 0 0-9 0v2.25h-.75A2.25 2.25 0 0 0 4.5 12.75v6A2.25 2.25 0 0 0 6.75 21h10.5a2.25 2.25 0 0 0 2.25-2.25v-6a2.25 2.25 0 0 0-2.25-2.25Zm-8.25-2.25a3 3 0 1 1 6 0v2.25H9V8.25Zm3 8.25a1.5 1.5 0 1 1 1.5-1.5 1.5 1.5 0 0 1-1.5 1.5Z" />
              </svg>
            </span>
            <input
              v-model="loginForm.password"
              type="password"
              autocomplete="current-password"
              placeholder="请输入密码"
            >
          </span>
        </label>

        <div v-if="captchaEnabled" class="login-form__field">
          <span class="login-form__label">验证码</span>
          <div class="login-form__captcha">
            <span class="login-form__control">
              <span class="login-form__icon" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <path d="M9 3.75A5.25 5.25 0 0 0 3.75 9v6A5.25 5.25 0 0 0 9 20.25h6A5.25 5.25 0 0 0 20.25 15V9A5.25 5.25 0 0 0 15 3.75H9Zm.75 4.5h4.5v1.5h-4.5v-1.5Zm0 3h4.5v1.5h-4.5v-1.5Zm0 3h3v1.5h-3v-1.5Z" />
                </svg>
              </span>
              <input
                v-model.trim="loginForm.code"
                type="text"
                autocomplete="off"
                placeholder="请输入验证码"
              >
            </span>
            <button
              type="button"
              class="login-form__captcha-image"
              @click="loadCaptcha"
            >
              <img :src="codeUrl" alt="验证码">
            </button>
          </div>
        </div>

        <label class="login-form__remember">
          <input v-model="loginForm.rememberMe" type="checkbox">
          <span>记住密码</span>
        </label>

        <p v-if="errorMessage" class="login-form__error">{{ errorMessage }}</p>

        <button
          type="submit"
          class="login-form__submit"
          :disabled="loading"
        >
          {{ loading ? '登 录 中...' : '登 录' }}
        </button>
      </form>
    </div>

    <div class="login-page__footer">
      Copyright © 2026 乌昌海关业务运行可视化
    </div>
  </div>
</template>

<script>
import { getCodeImg, getInfo, login } from '@/api/auth'
import {
  clearRememberedLogin,
  getRememberedLogin,
  setToken,
  setUserProfile,
  saveRememberedLogin
} from '@/utils/auth'

export default {
  name: 'LoginView',
  data() {
    return {
      backgroundImage: require('@/assets/login2.jpg'),
      codeUrl: '',
      loading: false,
      errorMessage: '',
      captchaEnabled: true,
      loginForm: {
        username: '',
        password: '',
        rememberMe: false,
        code: '',
        uuid: ''
      }
    }
  },
  created() {
    this.restoreRememberedLogin()
    this.loadCaptcha()
  },
  methods: {
    restoreRememberedLogin() {
      const remembered = getRememberedLogin()
      if (!remembered) {
        return
      }

      this.loginForm.username = remembered.username
      this.loginForm.password = remembered.password
      this.loginForm.rememberMe = remembered.rememberMe
    },
    async loadCaptcha() {
      try {
        const result = await getCodeImg()
        this.captchaEnabled = result.captchaEnabled === undefined ? true : result.captchaEnabled
        if (this.captchaEnabled) {
          this.codeUrl = `data:image/gif;base64,${result.img}`
          this.loginForm.uuid = result.uuid
        } else {
          this.codeUrl = ''
          this.loginForm.code = ''
          this.loginForm.uuid = ''
        }
      } catch (error) {
        this.errorMessage = error.message || '验证码加载失败'
      }
    },
    validateForm() {
      if (!this.loginForm.username) {
        this.errorMessage = '请输入账号'
        return false
      }
      if (!this.loginForm.password) {
        this.errorMessage = '请输入密码'
        return false
      }
      if (this.captchaEnabled && !this.loginForm.code) {
        this.errorMessage = '请输入验证码'
        return false
      }
      return true
    },
    async handleLogin() {
      this.errorMessage = ''
      if (!this.validateForm()) {
        return
      }

      this.loading = true
      try {
        const result = await login(this.loginForm)
        setToken(result.token)

        if (this.loginForm.rememberMe) {
          saveRememberedLogin(this.loginForm)
        } else {
          clearRememberedLogin()
        }

        try {
          const info = await getInfo(result.token)
          setUserProfile({ ...(info.user || {}), roles: info.roles || [], permissions: info.permissions || [] })
        } catch {
          setUserProfile({ userName: this.loginForm.username })
        }

        const redirect = this.$route.query.redirect || '/'
        this.$router.replace(redirect)
      } catch (error) {
        this.errorMessage = error.message || '登录失败，请稍后重试'
        await this.loadCaptcha()
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.login-page {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  height: 100%;
  padding: 32px 32px 32px clamp(64px, 8vw, 150px);
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  overflow: hidden;
}

.login-page::before {
  content: "";
  position: absolute;
  inset: 0;
  background:
    linear-gradient(90deg, rgba(6, 24, 55, 0.62) 0%, rgba(10, 39, 84, 0.4) 28%, rgba(10, 39, 84, 0.08) 58%, rgba(10, 39, 84, 0) 100%),
    radial-gradient(circle at 19% 22%, rgba(255, 255, 255, 0.22), rgba(255, 255, 255, 0) 28%);
  pointer-events: none;
}

.login-page__panel {
  width: 390px;
  max-width: calc(100vw - 32px);
  padding: 32px 32px 28px;
  border: 1px solid rgba(255, 255, 255, 0.52);
  border-radius: 18px;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.94), rgba(246, 250, 255, 0.84));
  box-shadow: 0 24px 70px rgba(5, 22, 48, 0.34);
  backdrop-filter: blur(10px);
  position: relative;
  z-index: 1;
  margin-top: 130px;
}

.login-page__heading {
  margin-bottom: 22px;
}

.login-page__heading h1 {
  margin: 10px 0 8px;
  color: #2f3542;
  font-size: 24px;
  line-height: 1.35;
}

.login-page__heading p {
  margin: 0;
  color: #6b7280;
  font-size: 14px;
}

.login-form__field {
  display: block;
  margin-bottom: 16px;
}

.login-form__label {
  display: inline-block;
  margin-bottom: 8px;
  color: #4b5563;
  font-size: 14px;
}

.login-form__control {
  display: flex;
  align-items: center;
  width: 100%;
  height: 44px;
  border: 1px solid #d8e2f0;
  border-radius: 8px;
  background: #fff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.login-form__control:focus-within {
  border-color: #1677ff;
  box-shadow: 0 0 0 3px rgba(22, 119, 255, 0.12);
}

.login-form__icon {
  width: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #8ea3bd;
  flex-shrink: 0;
}

.login-form__icon svg {
  width: 18px;
  height: 18px;
  fill: currentColor;
}

.login-form__control input {
  width: 100%;
  height: 100%;
  border: 0;
  outline: none;
  padding: 0 14px 0 0;
  color: #1f2937;
  font-size: 14px;
  background: transparent;
}

.login-form__captcha {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 112px;
  gap: 12px;
}

.login-form__captcha-image {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 112px;
  height: 44px;
  padding: 0;
  border: 1px solid #d8e2f0;
  border-radius: 8px;
  background: #f8fbff;
  cursor: pointer;
  overflow: hidden;
}

.login-form__captcha-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.login-form__remember {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 2px 0 18px;
  color: #4b5563;
  font-size: 14px;
  cursor: pointer;
}

.login-form__remember input {
  margin: 0;
}

.login-form__error {
  margin: 0 0 16px;
  color: #d93025;
  font-size: 13px;
}

.login-form__submit {
  width: 100%;
  height: 44px;
  border: 0;
  border-radius: 8px;
  background: linear-gradient(135deg, #1677ff, #0b57d0);
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
}

.login-form__submit:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 16px 30px rgba(22, 119, 255, 0.28);
}

.login-form__submit:disabled {
  opacity: 0.8;
  cursor: wait;
}

.login-page__footer {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 18px;
  text-align: center;
  color: rgba(255, 255, 255, 0.85);
  font-size: 12px;
  letter-spacing: 0.08em;
}

@media (max-width: 768px) {
  .login-page {
    justify-content: center;
    padding: 20px;
  }

  .login-page::before {
    background: linear-gradient(180deg, rgba(6, 24, 55, 0.52), rgba(6, 24, 55, 0.22));
  }

  .login-page__panel {
    width: 100%;
    margin-top: 0;
    padding: 28px 22px 24px;
  }

  .login-page__heading h1 {
    font-size: 24px;
  }

  .login-form__captcha {
    grid-template-columns: 1fr;
  }

  .login-form__captcha-image {
    width: 100%;
  }
}
</style>
